/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  ItemActionDetail,
  ItemFocusDetail,
  ItemHoverDetail,
  ItemInfo,
  TagCloudItemData
} from './tagCloud.type';

/**
 * Returns the item based on item info in a tag cloud.
 * @param itemInfo
 * @param items
 * @returns
 */
function getItem<K, D extends TagCloudItemData<K>>(itemInfo: ItemInfo, items: D[]) {
  const { idx } = itemInfo;
  return items[idx];
}

export function getItemPadding(height: number | undefined) {
  return height != null ? 0.15 * height : 0;
}

/**
 * Returns the next item's info in a tag cloud.
 * @param itemInfo
 * @param items
 * @returns
 */
function getNextItemInfo<K>(itemInfo: ItemInfo, items: TagCloudItemData<K>[]) {
  let idx = itemInfo.idx;

  idx = Math.min(items.length - 1, idx + 1);
  return { idx, id: items[idx].id };
}

/**
 * Returns the previous item's info in a tag cloud.
 * @param itemInfo
 * @param items
 * @returns
 */
function getPrevItemInfo<K>(itemInfo: ItemInfo, items: TagCloudItemData<K>[]) {
  let idx = itemInfo.idx;

  idx = Math.max(0, idx - 1);
  return { idx, id: items[idx].id };
}

/**
 * Returns utils functions for navigation in a tag cloud.
 * @param items
 * @returns
 */
export function getTagCloudNavUtils<K, D extends TagCloudItemData<K>>(items: D[]) {
  return {
    getItem: (itemInfo: ItemInfo) => {
      return getItem(itemInfo, items);
    },
    getDetailFromInfo: (
      itemInfo?: ItemInfo
    ): ItemFocusDetail<K, D> | ItemHoverDetail<K, D> | ItemActionDetail<K, D> => {
      if (!itemInfo) {
        return { id: undefined, data: undefined };
      }
      const item = getItem(itemInfo, items);
      return { id: item.id, data: item };
    },
    getPrevItemInfo: (itemInfo: ItemInfo) => {
      return getPrevItemInfo(itemInfo, items);
    },
    getNextItemInfo: (itemInfo: ItemInfo) => {
      return getNextItemInfo(itemInfo, items);
    }
  };
}
